/**
 * Dialog page — the core learning assessment experience.
 *
 * Designed as a Two-Zone Stage:
 * - Left Zone: Question prompt with KaTeX math, dynamic figures, and prior question review.
 * - Right Zone: Answer deck with handwriting canvas or typed math + symbol toolbar,
 *   working notes, confidence rating, and sticky action dock.
 */
import { useState, useEffect, useCallback, useRef } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { apiPost } from "../../api/client";
import { useAuth } from "../../auth/AuthContext";
import { Figure } from "../../components/Figure";
import { MathText } from "../../components/Math";
import { HandwritingPad, padHasInk, padToPng } from "../../components/HandwritingPad";
import { MathsAnswerInput, defaultAnswerMode, type MathsAnswerMode } from "../../components/MathsAnswerInput";
import { McqChoices } from "../../components/McqChoices";
import type {
  DialogStartResponse,
  DialogRespondResponse,
  DialogQuestion,
} from "../../types";

type Phase = "loading" | "question" | "complete" | "submitting-final";

type AnsweredStep = {
  number: number;
  question: DialogQuestion;
  answer: string;
  working: string;
  handwrittenAnswer: string;
};

export function DialogPage() {
  const { cid, lid } = useParams<{ cid: string; lid: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [phase, setPhase] = useState<Phase>("loading");
  const [sessionId, setSessionId] = useState<string>("");
  const [lessonTitle, setLessonTitle] = useState("");
  const [totalQuestions, setTotalQuestions] = useState(0);
  const [currentNumber, setCurrentNumber] = useState(1);
  const [question, setQuestion] = useState<DialogQuestion | null>(null);
  const [answer, setAnswer] = useState("");
  const [working, setWorking] = useState("");
  const [handwrittenAnswer, setHandwrittenAnswer] = useState("");
  const [mathsAnswerMode, setMathsAnswerMode] = useState<MathsAnswerMode>("handwrite");
  const [confidence, setConfidence] = useState<string>("medium");
  const [hint, setHint] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [blocked, setBlocked] = useState(false);
  const [attemptsRemaining, setAttemptsRemaining] = useState<number>(-1);
  const [lessonPassed, setLessonPassed] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [answered, setAnswered] = useState<AnsweredStep[]>([]);
  const [reviewing, setReviewing] = useState<number | null>(null);

  const startTime = useRef(Date.now());
  const sessionStart = useRef(Date.now());
  const startedFor = useRef<string | null>(null);

  useEffect(() => {
    if (!cid || !lid || !user) return;
    const key = `${cid}/${lid}`;
    if (startedFor.current === key) return;
    startedFor.current = key;
    apiPost<DialogStartResponse>("/api/dialog/start", {
      learner_id: user.id,
      curriculum_id: cid,
      lesson_id: lid,
    })
      .then((data) => {
        setSessionId(data.session_id);
        setLessonTitle(data.lesson_title);
        setTotalQuestions(data.total_questions);
        setQuestion(data.current_question);
        setMathsAnswerMode(defaultAnswerMode(data.current_question?.answer_mode));
        setAttemptsRemaining(data.attempts_remaining);
        setLessonPassed(Boolean(data.is_complete));
        setPhase("question");
        startTime.current = Date.now();
        sessionStart.current = Date.now();
      })
      .catch((err) => {
        if (err?.status === 403) setBlocked(true);
        setError(err.message);
        startedFor.current = null;
      });
  }, [cid, lid, user?.id]);

  useEffect(() => {
    if (phase !== "question") return;
    const id = setInterval(
      () => setElapsed(Math.floor((Date.now() - sessionStart.current) / 1000)),
      1000
    );
    return () => clearInterval(id);
  }, [phase]);

  const submitResponse = useCallback(async () => {
    // Any written question can be handwritten; multiple choice cannot.
    const isWritten = !question?.choices?.length;
    const hasHandwrittenAnswer = isWritten && padHasInk(handwrittenAnswer);
    const hasTypedAnswer = Boolean(answer.trim());

    if (isWritten && !hasHandwrittenAnswer && !hasTypedAnswer) {
      setError("Please handwrite or type your answer before submitting.");
      return;
    }
    if (!isWritten && !hasTypedAnswer) {
      setError("Please write your answer before submitting.");
      return;
    }
    setError("");
    setSubmitting(true);
    setHint(null);

    const confMap: Record<string, number> = { high: 5, medium: 3, low: 1 };
    const timeSpent = (Date.now() - startTime.current) / 1000;
    const answerImage = hasHandwrittenAnswer
      ? padToPng(handwrittenAnswer, 1200, { maxWidth: 1400 })
      : null;

    try {
      const result = await apiPost<DialogRespondResponse>(
        `/api/dialog/${sessionId}/respond`,
        {
          response_text: answer,
          working: working.trim() || undefined,
          answer_ink: hasHandwrittenAnswer ? handwrittenAnswer : undefined,
          answer_image: answerImage || undefined,
          confidence: confMap[confidence] ?? 3,
          time_spent_seconds: timeSpent,
        }
      );

      if (result.follow_up_hint) {
        setHint(result.follow_up_hint);
      }

      if (question) {
        setAnswered((prev) => [
          ...prev,
          { number: currentNumber, question, answer, working, handwrittenAnswer },
        ]);
      }

      if (result.has_more_questions && result.next_question) {
        setCurrentNumber((n) => n + 1);
        setQuestion(result.next_question);
        setAnswer("");
        setWorking("");
        setHandwrittenAnswer("");
        setMathsAnswerMode(defaultAnswerMode(result.next_question.answer_mode));
        startTime.current = Date.now();
      } else {
        setElapsed(Math.floor((Date.now() - sessionStart.current) / 1000));
        setPhase("complete");
      }
    } catch (err: any) {
      setError(err.message || "Failed to submit response");
    } finally {
      setSubmitting(false);
    }
  }, [answer, confidence, currentNumber, handwrittenAnswer, question, sessionId, working]);

  const completeDialog = useCallback(async () => {
    setPhase("submitting-final");
    try {
      const result = await apiPost(`/api/dialog/${sessionId}/complete`);
      sessionStorage.setItem("dialog_results", JSON.stringify(result));
      sessionStorage.setItem("dialog_lesson_id", lid || "");
      navigate(`/learn/${cid}/summary/${sessionId}`, { replace: true });
    } catch (err: any) {
      setError(err.message || "Failed to complete dialog");
      setPhase("complete");
    }
  }, [sessionId, cid, lid, navigate]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.ctrlKey && e.key === "Enter") {
      submitResponse();
    }
  };

  const progressPct = totalQuestions > 0 ? (currentNumber / totalQuestions) * 100 : 0;

  if (phase === "loading") {
    return (
      <div className="dialog-page">
        {blocked ? (
          <div className="card error-card">
            <h2>Lesson locked</h2>
            <p role="alert">{error}</p>
            <Link to={`/learn/${cid}/lesson/${lid}`} className="btn btn-primary">
              ← Back to Lesson
            </Link>
          </div>
        ) : error ? (
          <div className="card error-card">
            <h2>Error</h2>
            <p role="alert">{error}</p>
          </div>
        ) : (
          <p className="text-muted" role="status">Starting dialogue session...</p>
        )}
      </div>
    );
  }

  return (
    <div className="dialog-page" onKeyDown={handleKeyDown}>
      {/* Navigation & Header */}
      <div className="dialog-header-bar">
        <Link to={`/learn/${cid}/lesson/${lid}`} className="breadcrumb-link">
          ← Back to Lesson
        </Link>
        <div className="dialog-timer-badge" title="Elapsed Time in Session">
          ⏱ {Math.floor(elapsed / 60)}:{String(elapsed % 60).padStart(2, "0")}
        </div>
      </div>

      {/* Progress Track */}
      <div className="dialog-progress">
        <div className="dialog-progress-label">
          <span>Question {currentNumber} of {totalQuestions}</span>
          {lessonTitle && <span className="lesson-title"> · {lessonTitle}</span>}
          {/* The attempt cap applies whether or not the lesson has been passed,
              so the count is always the true thing to show. "Passed" is worth
              saying too, but never as "revisit freely" — it isn't free. */}
          {attemptsRemaining >= 0 ? (
            <span className="attempts-badge">
              {lessonPassed ? " · Previously passed" : ""} · {attemptsRemaining} attempt
              {attemptsRemaining === 1 ? "" : "s"} remaining (including this one)
            </span>
          ) : lessonPassed ? (
            <span className="attempts-badge"> · Previously passed</span>
          ) : null}
        </div>
        <div className="progress-track">
          <div
            className="progress-fill"
            style={{ width: `${phase === "complete" ? 100 : progressPct}%` }}
          />
        </div>
      </div>

      {/* Answered Questions History Bar */}
      {answered.length > 0 && (
        <div className="answered-steps-bar">
          <span className="answered-steps-label">Completed Questions:</span>
          <div className="answered-steps-list">
            {answered.map((step, idx) => (
              <button
                key={idx}
                type="button"
                className={`answered-step-pill ${reviewing === idx ? "active" : ""}`}
                onClick={() => setReviewing(reviewing === idx ? null : idx)}
              >
                Q{step.number}
              </button>
            ))}
            {reviewing !== null && (
              <button
                type="button"
                className="answered-step-close"
                onClick={() => setReviewing(null)}
              >
                ✕ Close Review
              </button>
            )}
          </div>
        </div>
      )}

      {/* Review View */}
      {reviewing !== null && answered[reviewing] && (
        <div className="card question-review-card">
          <div className="question-type-badge">
            Reviewing Question {answered[reviewing].number} · {answered[reviewing].question.type}
            {answered[reviewing].question.ai_generated && (
              <span className="badge badge-primary" style={{ marginLeft: 8 }}>AI drafted</span>
            )}
          </div>
          <div className="question-text">
            <MathText content={answered[reviewing].question.text} />
          </div>
          {answered[reviewing].question.figure && (
            <div className="question-figure">
              <Figure spec={answered[reviewing].question.figure!} height={280} maxWidth={440} />
            </div>
          )}
          <div className="reviewed-answer-box">
            <strong>Your Submitted Answer:</strong>
            {answered[reviewing].answer && (
              <div className="reviewed-typed">
                <MathText content={answered[reviewing].answer} />
              </div>
            )}
            {answered[reviewing].handwrittenAnswer && (
              <HandwritingPad
                value={answered[reviewing].handwrittenAnswer}
                onChange={() => undefined}
                disabled
              />
            )}
          </div>
          <button
            type="button"
            className="btn btn-secondary btn-sm"
            onClick={() => setReviewing(null)}
          >
            ← Back to Active Question
          </button>
        </div>
      )}

      {/* Two-Zone Stage */}
      {reviewing === null && phase === "question" && question && (
        <div className="dialog-stage-grid">
          {/* Left Zone: Question & Visuals */}
          <div className="card dialog-stage-prompt">
            <div className="prompt-header">
              <span className="question-index-tag">Question {currentNumber}</span>
              <span className="question-type-badge">{question.type}</span>
              {question.ai_generated && (
                <span className="badge badge-primary">AI drafted</span>
              )}
            </div>

            <div className="question-text">
              <MathText content={question.text} />
            </div>

            {question.figure && (
              <figure className="question-figure" aria-label="Question Diagram">
                {question.figure.title && (
                  <figcaption className="figure-title">{question.figure.title}</figcaption>
                )}
                <Figure spec={question.figure} height={280} maxWidth={460} />
                {question.figure.caption && (
                  <figcaption className="figure-caption">{question.figure.caption}</figcaption>
                )}
              </figure>
            )}

            {hint && (
              <div className="follow-up-hint">
                <span className="hint-label">Teacher Remark:</span>
                <MathText inline content={hint} />
              </div>
            )}
          </div>

          {/* Right Zone: Answer Deck */}
          <div className="card dialog-stage-answer">
            {question.choices && question.choices.length > 0 ? (
              <div className="mcq-section">
                <span className="field-label">Select your answer:</span>
                <McqChoices
                  choices={question.choices}
                  selected={answer}
                  onSelect={(choice) => {
                    setAnswer(choice);
                    if (hint) setHint(null);
                  }}
                  disabled={submitting}
                />
              </div>
            ) : (
              <MathsAnswerInput
                maths={question.answer_mode === "handwriting"}
                answer={answer}
                onAnswerChange={setAnswer}
                ink={handwrittenAnswer}
                onInkChange={setHandwrittenAnswer}
                mode={mathsAnswerMode}
                onModeChange={setMathsAnswerMode}
                working={working}
                onWorkingChange={setWorking}
                disabled={submitting}
              />
            )}

            {/* Confidence Selector */}
            <div className="confidence-picker">
              <span className="confidence-label">Confidence:</span>
              <div className="confidence-options">
                {[
                  { id: "low", label: "Low" },
                  { id: "medium", label: "Medium" },
                  { id: "high", label: "High" },
                ].map((opt) => (
                  <button
                    key={opt.id}
                    type="button"
                    className={`confidence-btn ${confidence === opt.id ? "active" : ""}`}
                    onClick={() => setConfidence(opt.id)}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Error Message */}
            {error && <p className="dialog-error-text" role="alert">{error}</p>}

            {/* Action Dock */}
            <div className="dialog-action-dock">
              <button
                type="button"
                className="btn btn-primary btn-submit-answer"
                onClick={submitResponse}
                disabled={submitting}
              >
                {submitting
                  ? "Evaluating..."
                  : currentNumber === totalQuestions
                  ? "Submit & Finish Question →"
                  : "Submit Response →"}
              </button>
              <span className="shortcut-hint">Tip: Ctrl + Enter to submit</span>
            </div>
          </div>
        </div>
      )}

      {/* Completion Screen */}
      {phase === "complete" && (
        <div className="card dialog-completion-card">
          <div className="completion-badge">✓ Dialogue Finished</div>
          <h2>All questions answered!</h2>
          <p className="completion-desc">
            You've completed all {totalQuestions} questions for <strong>{lessonTitle}</strong>.
            Submit your session to receive your marked memorandum and rubric analysis.
          </p>
          <div className="completion-actions">
            <button
              type="button"
              className="btn btn-primary btn-lg"
              onClick={completeDialog}
            >
              Generate Marked Rubric &amp; Marks →
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
